import { useEffect, useRef } from 'react';
import { useToast } from '@/components/ui/use-toast';

export function ServiceWorkerUpdateToast() {
  const { toast } = useToast();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reloadTriggeredRef = useRef(false);

  useEffect(() => {
    const handleSwUpdateAvailable = (event: any) => {
      // Si ya se mostró la notificación en esta sesión, no mostrar de nuevo
      const alreadyShown = sessionStorage.getItem('sw-update-shown');
      if (alreadyShown) {
        return;
      }

      const registration = event.detail?.registration;
      const waitingWorker = event.detail?.waitingWorker;

      const performHardRefresh = async () => {
        if (reloadTriggeredRef.current) {
          return;
        }
        reloadTriggeredRef.current = true;

        // Limpia únicamente Cache Storage para forzar assets nuevos.
        // No se toca localStorage para preservar sesión y preferencias.
        try {
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((key) => caches.delete(key)));
          }
        } catch (error) {
          console.warn('[SW Update] No se pudo limpiar Cache Storage:', error);
        }

        const url = new URL(window.location.href);
        url.searchParams.set('swv', Date.now().toString());

        // replace() fuerza navegación de documento (no solo re-render de SPA).
        window.location.replace(url.toString());

        // Fallback por si el navegador/PWA ignora el replace inmediatamente.
        timeoutRef.current = setTimeout(() => {
          window.location.reload();
        }, 800);
      };

      const handleReload = async () => {
        // Marcar que ya se hizo click para no volver a mostrar
        sessionStorage.setItem('sw-update-shown', 'true');
        sessionStorage.setItem('sw-update-in-progress', 'true');

        // Cancelar cualquier timeout pendiente
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // 1. Intentar traer estado fresco de registration.
        try {
          await registration?.update();
        } catch {
          // Si falla update(), continuamos con el worker disponible.
        }

        // 2. Esperar toma de control del nuevo SW, pero con timeout para no quedar colgados.
        const waitForControllerChange = new Promise<void>((resolve) => {
          let settled = false;
          const done = () => {
            if (settled) return;
            settled = true;
            resolve();
          };

          navigator.serviceWorker.addEventListener('controllerchange', done, { once: true });
          timeoutRef.current = setTimeout(done, 2500);
        });

        // 3. Señalizar al nuevo Service Worker para saltar la espera.
        const workerToSkip = registration?.waiting || waitingWorker || registration?.installing;
        if (workerToSkip) {
          workerToSkip.postMessage({ type: 'SKIP_WAITING' });
        }

        await waitForControllerChange;
        await performHardRefresh();
      };

      // Marcar que se mostró la notificación
      sessionStorage.setItem('sw-update-shown', 'true');

      // Mostrar aviso breve y aplicar la actualización automáticamente.
      toast({
        title: '✨ Actualizando Minibid...',
        description: 'La app se va a recargar en unos segundos.',
        placement: 'top',
      });

      // Iniciar la actualización tras 1.5 s para que el toast sea visible.
      timeoutRef.current = setTimeout(() => {
        void handleReload();
      }, 1500);
    };

    window.addEventListener('sw-update-available', handleSwUpdateAvailable);

    return () => {
      window.removeEventListener('sw-update-available', handleSwUpdateAvailable);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [toast]);

  return null;
}
